// ClearRoute brand logo — a vector rebuild of the navy/teal "route forms a C"
// mark plus the ClearRoute wordmark. Vector so it stays crisp at every size,
// recolours for dark backgrounds (the sidebar) and drives the favicon.
//
// To use the exact raster artwork instead, drop the PNGs into public/brand/
// and swap <LogoMark/> for an <img src="/brand/clearroute-icon.png" />.
export const BRAND_NAVY = '#0E3A63'
export const BRAND_TEAL = '#17A398'

// The icon on its own (square). `size` is the rendered height/width in px.
// `tone='dark'` lightens the navy parts so the mark stays legible on the dark
// sidebar (plain navy would disappear against near-black).
export function LogoMark({ size = 32, tone = 'light', className = '', title = 'ClearRoute' }) {
  const navy = tone === 'dark' ? '#E2E8F0' : BRAND_NAVY
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label={title}
    >
      {/* Teal C — the open route loop */}
      <path d="M47.6 16.4 A22 22 0 1 0 47.6 47.6" stroke={BRAND_TEAL} strokeWidth="9" strokeLinecap="round" fill="none" />
      {/* Navy sweep over the top / upper-left of the C */}
      <path d="M47.6 16.4 A22 22 0 0 0 10 32" stroke={navy} strokeWidth="9" strokeLinecap="round" fill="none" />
      {/* Teal waypoint dot (top) */}
      <circle cx="50" cy="14" r="4.5" fill={BRAND_TEAL} />
      {/* Navy hub dot (centre) */}
      <circle cx="25" cy="33" r="6" fill={navy} />
    </svg>
  )
}

// Full brand lockup. Variants:
//  - 'lockup'   icon + "ClearRoute" (default)
//  - 'full'     icon + "ClearRoute" + "MANAGEMENT" strap
//  - 'wordmark' text only
//  - 'icon'     mark only
// `tone` = 'light' (navy text, for light backgrounds) or 'dark' (white text,
// for the dark sidebar). `size` is the icon height; the lockup scales from it.
//
// Lockup/full/wordmark variants render the exact approved artwork (the PNG);
// only the square 'icon' slot uses the vector mark, since the artwork is a
// wide horizontal lockup that can't stand in as a square icon.
export const LOGO_PNG = '/clearroute-management-logo.png'

export default function Logo({ variant = 'lockup', tone = 'light', size = 30, className = '' }) {
  if (variant === 'icon') return <LogoMark size={size} tone={tone} className={className} />

  // Height scales from `size` (the icon height the call sites already pass);
  // width follows the artwork's ~2.5:1 aspect ratio automatically.
  const height = Math.round(size * (variant === 'full' ? 2.0 : 1.7))
  const img = (
    <img
      src={LOGO_PNG}
      alt="ClearRoute Management"
      style={{ height, width: 'auto', display: 'block' }}
    />
  )

  // The artwork is dark-inked, so on dark backgrounds (the sidebar) sit it on
  // a white chip for contrast.
  if (tone === 'dark') {
    return (
      <span className={`inline-flex bg-white rounded-md ${className}`} style={{ padding: '5px 8px' }}>
        {img}
      </span>
    )
  }
  return <span className={`inline-flex items-center ${className}`}>{img}</span>
}
